import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PenSquare, Eye, Ban, Trash2, ScrollText, ChevronDown, ChevronRight, ShieldCheck } from "lucide-react";

const ACTION_LABEL = {
  uploaded: ["Uploaded", "bg-green-500/15 text-green-400"],
  replaced: ["Replaced", "bg-blue-500/15 text-blue-400"],
  removed: ["Removed", "bg-red-500/15 text-red-400"],
  retired: ["Retired", "bg-amber-500/15 text-amber-400"],
  consent_granted: ["Consent on", "bg-emerald-500/15 text-emerald-400"],
  consent_revoked: ["Consent off", "bg-zinc-500/15 text-zinc-400"],
  applied: ["Applied", "bg-indigo-500/15 text-indigo-400"],
  default_authority_set: ["Default set", "bg-purple-500/15 text-purple-300"],
};
const fmt = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString(); } catch { return d; } };

export default function SignaturesAdmin() {
  const qc = useQueryClient();
  const [viewId, setViewId] = useState(null);
  const [showLog, setShowLog] = useState(false);

  const { data: rows = [] } = useQuery({ queryKey: ["signatures-all"], queryFn: async () => (await api.get("/signatures/all")).data });
  const { data: consented = [] } = useQuery({ queryKey: ["signatures-consented"], queryFn: async () => (await api.get("/signatures/consented-users")).data });
  const { data: authority } = useQuery({ queryKey: ["sig-default-authority"], queryFn: async () => (await api.get("/signatures/default-authority")).data });
  const { data: log = [] } = useQuery({ queryKey: ["signatures-audit"], queryFn: async () => (await api.get("/signatures/audit")).data, enabled: showLog });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["signatures-all"] });
    qc.invalidateQueries({ queryKey: ["signatures-consented"] });
    qc.invalidateQueries({ queryKey: ["signatures-audit"] });
  };
  const fail = (e, fb) => toast.error(formatApiError(e) || fb);

  const authorityMut = useMutation({
    mutationFn: async (uid) => (await api.put("/signatures/default-authority", { user_id: uid === "none" ? "" : uid })).data,
    onSuccess: () => { toast.success("Default signing authority updated"); qc.invalidateQueries({ queryKey: ["sig-default-authority"] }); qc.invalidateQueries({ queryKey: ["signatures-audit"] }); },
    onError: (e) => fail(e, "Could not update"),
  });
  const retireMut = useMutation({
    mutationFn: async (uid) => (await api.post(`/signatures/user/${uid}/retire`)).data,
    onSuccess: () => { toast.success("Signature retired"); refresh(); },
    onError: (e) => fail(e, "Could not retire"),
  });
  const removeMut = useMutation({
    mutationFn: async (uid) => (await api.delete(`/signatures/user/${uid}`)).data,
    onSuccess: () => { toast.success("Signature removed"); refresh(); },
    onError: (e) => fail(e, "Could not remove"),
  });

  const withSig = rows.filter((r) => r.has_signature);

  return (
    <div className="border border-border bg-card" data-testid="signatures-admin">
      <div className="px-8 py-6 border-b border-border">
        <div className="flex items-center gap-2"><PenSquare size={18} className="text-primary" /><div className="label-mono">Admin</div></div>
        <h2 className="font-heading text-2xl mt-1">Signatures</h2>
        <p className="text-sm text-muted-foreground mt-2">Oversight only. A person sets or changes their own signature from the <span className="text-foreground">My signature</span> menu — you can view, retire or remove, but never sign or consent on anyone's behalf.</p>
      </div>

      <div className="px-8 py-5 border-b border-border">
        <div className="flex items-center gap-2 mb-2"><ShieldCheck size={15} className="text-primary" /><span className="text-sm font-medium">Default signing authority</span></div>
        <p className="text-xs text-muted-foreground mb-2.5">Used on documents that have no obvious owner. Only users who have a signature with auto-apply on can be chosen.</p>
        <Select value={authority?.user_id || "none"} onValueChange={(v) => authorityMut.mutate(v)}>
          <SelectTrigger className="w-80" data-testid="default-authority-select"><SelectValue placeholder="— None —" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— None —</SelectItem>
            {consented.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}{u.title ? ` · ${u.title}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Signature on file</TableHead>
              <TableHead>Auto-apply</TableHead>
              <TableHead>Last updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {withSig.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No signatures on file yet.</TableCell></TableRow>
            ) : withSig.map((r) => (
              <TableRow key={r.user_id} data-testid={`sig-row-${r.user_id}`}>
                <TableCell>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.printed_name}{r.title ? ` · ${r.title}` : ""}</div>
                </TableCell>
                <TableCell>
                  {r.is_active
                    ? <Badge className="border-0 bg-green-500/15 text-green-400">On file</Badge>
                    : <Badge className="border-0 bg-amber-500/15 text-amber-400">Retired</Badge>}
                </TableCell>
                <TableCell>
                  {r.auto_apply_consent
                    ? <Badge className="border-0 bg-emerald-500/15 text-emerald-400">On</Badge>
                    : <Badge className="border-0 bg-zinc-500/15 text-zinc-400">Off</Badge>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{fmt(r.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="View" onClick={() => setViewId(r.user_id)} data-testid={`sig-view-${r.user_id}`}><Eye className="h-4 w-4" /></Button>
                  {r.is_active && <Button variant="ghost" size="icon" title="Retire" onClick={() => { if (window.confirm(`Retire ${r.name}'s signature? It will stop being applied.`)) retireMut.mutate(r.user_id); }} data-testid={`sig-retire-${r.user_id}`}><Ban className="h-4 w-4 text-amber-500" /></Button>}
                  <Button variant="ghost" size="icon" title="Remove" onClick={() => { if (window.confirm(`Remove ${r.name}'s signature entirely?`)) removeMut.mutate(r.user_id); }} data-testid={`sig-remove-${r.user_id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="border-t border-border">
        <button onClick={() => setShowLog((s) => !s)} className="w-full px-8 py-4 flex items-center gap-2 hover:bg-secondary/40 transition-colors" data-testid="signature-log-toggle">
          {showLog ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <ScrollText className="h-4 w-4 text-muted-foreground" />
          <span className="label-mono">Signature activity log</span>
        </button>
        {showLog && (
          <div className="px-8 pb-5" data-testid="signature-activity-log">
            {log.length === 0 ? <p className="text-sm text-muted-foreground py-2">No activity yet.</p> : (
              <ol className="space-y-1.5">
                {log.map((e) => {
                  const [label, cls] = ACTION_LABEL[e.action] || [e.action, "bg-zinc-500/15 text-zinc-400"];
                  return (
                    <li key={e.id} className="text-sm border-l-2 border-border pl-3 py-0.5 flex items-center gap-2 flex-wrap" data-testid={`sig-log-${e.id}`}>
                      <Badge className={`border-0 text-[10px] ${cls}`}>{label}</Badge>
                      {e.target_user_name && <span className="text-xs">{e.target_user_name}</span>}
                      {e.doc_type && <span className="text-xs text-muted-foreground">· {e.doc_type.replace(/_/g, " ")}{e.doc_id ? ` #${String(e.doc_id).slice(0, 8)}` : ""}{e.override_used ? " · override" : ""}</span>}
                      <span className="text-xs text-muted-foreground">· by {e.actor_name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{fmt(e.at)}</span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        )}
      </div>

      <ViewDialog userId={viewId} onClose={() => setViewId(null)} />
    </div>
  );
}

function ViewDialog({ userId, onClose }) {
  const { data } = useQuery({
    queryKey: ["signature-user", userId],
    queryFn: async () => (await api.get(`/signatures/user/${userId}`)).data,
    enabled: !!userId,
  });
  if (!userId) return null;
  const sig = data?.signature;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="sig-view-dialog">
        <DialogHeader>
          <DialogTitle>{data?.name || "Signature"}</DialogTitle>
          <DialogDescription>Read-only. Only {data?.name || "the owner"} can change this signature.</DialogDescription>
        </DialogHeader>
        {sig?.image ? (
          <div className="rounded-lg bg-white border border-border p-4 flex items-center justify-center h-32">
            <img src={sig.image} alt="signature" className="max-h-full max-w-full object-contain" data-testid="sig-view-image" />
          </div>
        ) : <p className="text-sm text-muted-foreground">No image on file.</p>}
        {sig && (
          <div className="text-sm text-muted-foreground">
            <div><span className="text-foreground">{sig.printed_name}</span>{sig.title ? ` · ${sig.title}` : ""}</div>
            <div className="mt-1">Auto-apply: {sig.auto_apply_consent ? "On" : "Off"} · {sig.is_active ? "Active" : "Retired"}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
